import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  damageFollowUpSchema,
  INIT_DAMAGE_FOLLOW_UP_FORM_VALUE,
} from "../../schema/prInvestigation";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { compareObjects } from "../../helpers";
import { IDamageFollowUp } from "../../models/prException";
import { DamageFollowupAPI } from "../../API/DamageFollowupAPI";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import PRInvestigationTemplate from "../../templates/prInvestigationTemplate";
import NoteBox from "../../components/noteBox/NoteBox";
import { useImageUploader } from "../../@hooks/useImageUploader";
import { hasOnHandDeliveryPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { FORM_STYLES,
  DAMAGE_FOLLOW_UP_FEILDS,
  EPRStatus,
  AppURLs,
  EFormAction,
  EOsdType,
  ErrorMessages
 } from "../../constants";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  setIsCompleteAndImage?: (isComplete: boolean) => void;
  osdType?: string;
  isEditMode?: boolean;
};


const DamageFollowUp: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  setIsCompleteAndImage,
  osdType,
  isEditMode = false,
}) => {
  const dispatch = useDispatch();
  const { uploadImage } = useImageUploader();
  const isRF = osdType === EOsdType.rf;
  const isPR = osdType === EOsdType.pr;
  const investigationDetails = useMemo(() => osdDetails, [osdDetails]);
  const hasOnHandPermission = useSelector(hasOnHandDeliveryPermissionSelector);
  const [loading, setLoading] = useState(false);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString()
  );
  const [status, setStatus] = useState(0);
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(true);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<IDamageFollowUp>(
    INIT_DAMAGE_FOLLOW_UP_FORM_VALUE
  );

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: damageFollowUpSchema,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (_values) => {
      // Handle form submission
      setIsCompleting(true);
    },
  });

  const snackbarvalueRangeFollowUpMessages: Record<string, string> = {
    "1": "Note: Send to your salvage vendor on a suffix pro",
    "2": "Note: Contact your senior for further instructions",
    "3": "Note: Issue the on-hand notice and follow on-hand procedures",
  };

  const valueRangeFollowUpMessage =
    values?.doesShipperWantFreightBack === "N"
      ? snackbarvalueRangeFollowUpMessages[values?.valueRangeFollowUp ?? ""]
      : null;

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined
  ) => {
    setInitValues((prevState) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  // Follow-up action
// --- Helpers ---

const removeFields = (
  params: IDamageFollowUp,
  errors: Record<string, unknown>,
  fields: (keyof IDamageFollowUp)[]
) => {
  fields.forEach((key) => {
    delete params[key];
    delete errors[key];
  });
};

const normalizeYesNoFields = (params: IDamageFollowUp) => {
  ([
    "doesShipperWantFreightBack",
    "didRcpmApproveDumpBill",
    "wasReturnAuthorizationReceived",
    "clearShipmentStatusRCN",
  ] as (keyof IDamageFollowUp)[]).forEach((key) => {
    if (!["Y", "N"].includes(params[key] as string)) {
      delete params[key];
    }
  });
};

const pruneConditionalFields = (
  params: IDamageFollowUp,
  errors: Record<string, unknown>,
  isRF: boolean,
  isPR: boolean,
  values: IDamageFollowUp
) => {
  if (isRF) {
    const fieldsToDelete: (keyof IDamageFollowUp)[] = [
      "didRcpmApproveDumpBill",
      "valueRangeFollowUp",
    ];
    if (values?.doesShipperWantFreightBack === "N") {
      fieldsToDelete.push("freightBackDetails");
    }
    removeFields(params, errors, fieldsToDelete);
  }

  if (isPR) {
    removeFields(params, errors, ["clearShipmentStatusRCN"]);
  }

  if (params.doesShipperWantFreightBack === "N") {
    delete params.clearShipmentStatusRCN;
  } else if (params.doesShipperWantFreightBack === "Y") {
    removeFields(params, errors, [
      "valueRangeFollowUp",
      "didRcpmApproveDumpBill",
      "wasReturnAuthorizationReceived",
    ]);
  }
};

const handleApiSuccess = async (
  statusNumber: number,
  params: IDamageFollowUp,
  action: EFormAction,
  dispatch: any,
  osdNumber: number,
  enterOH: boolean
) => {
  setStatus(statusNumber);
  setIsCompleting(false);
  setInvestigationStatus(statusNumber);
  setFormFieldsDisabled(true);
  patchFormFieldValues(params);

  const message =
    action === EFormAction.complete
      ? ErrorMessages.investigationNoteCompleted
      : ErrorMessages.prFollowUpSuccess;

  dispatch(
    PopoutNotification(
      NOTIFICATION_TYPE.SUCCESS,
      message,
      Date.now().toString()
    )
  );

  if (action === EFormAction.complete) {
    setIsCompleteAndImage?.(true);
    await uploadImage({
      elementId: "damage-followup-image-temp",
      osdNumber,
      setLoading,
      navigationUrl: enterOH ? AppURLs.ohEntry : "",
      state: { state: { osdNumber } },
    });
  }
};

const handleApiError = (err: unknown, dispatch: any) => {
  const errorMessage =
    typeof err === "string"
      ? err
      : "Exception follow-up failed. Please try again.";

  dispatch(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      errorMessage,
      Date.now().toString()
    )
  );
  console.error("Exception follow-up error:", err);
};

// --- Main function ---

const saveOrComplete = useCallback(
  async (action = EFormAction.followupSave, enterOH: boolean = false) => {
    const params: IDamageFollowUp = { ...values };
    let errors = await validateForm();

    normalizeYesNoFields(params);
    pruneConditionalFields(params, errors, isRF, isPR, values);

    const hasValidationError = Object.keys(errors).length > 0;
    if (action !== EFormAction.followupSave && hasValidationError) {
      setIsCompleting(true);
      return;
    }

    setLoading(true);
    try {
      const { statusNumber } = await DamageFollowupAPI(
        params,
        osdNumber,
        action
      );
      await handleApiSuccess(
        statusNumber,
        params,
        action,
        dispatch,
        osdNumber,
        enterOH
      );
    } catch (err) {
      handleApiError(err, dispatch);
    } finally {
      setLoading(false);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [values, dispatch, validateForm, osdNumber, isRF, isPR, osdType]
);


  const resetToLastSaved = () => {
    resetForm(); // Reset to last saved values
    setResetTimestamps(Date.now().toString());
    patchFormFieldValues(initValues);
  };

  const patchFormFieldValues = (formValues: IDamageFollowUp) => {
    DAMAGE_FOLLOW_UP_FEILDS.forEach((key: keyof IDamageFollowUp) => {
      const value = formValues[key] ?? undefined;
      setFieldValue(key, value, true);
      updateInitValue(key, value);
    });
  };

  const getErrorMessage = (field: keyof IDamageFollowUp) => {
    if (errors?.[field] && isCompleting) {
      return <span className="form-error">{errors?.[field]}</span>;
    }
    return null;
  };

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (isEditMode) {
      setFormFieldsDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    if (!investigationDetails) return;
    patchFormFieldValues(investigationDetails);
    if (investigationDetails?.investigationStatusNumber)
      setStatus(investigationDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <>
            <Grid item md={12} xs={12} className="pt-0">
              <FormControl className="radio-group question-group">
                <FormLabel>
                  Does the shipper want the freight back? <sup>*</sup>
                </FormLabel>
                <RadioGroup
                  row
                  name="doesShipperWantFreightBack"
                  data-testid="doesShipperWantFreightBack"
                  value={values?.doesShipperWantFreightBack ?? ""}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="Y"
                    control={<Radio disabled={formFieldsDisabled} />}
                    label="Yes"
                    data-testid="doesShipperWantFreightBackY"
                    checked={values?.doesShipperWantFreightBack === "Y"}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("valueRangeFollowUp", undefined);
                      setFieldValue(
                        "clearShipmentStatusRCN",
                        initValues.clearShipmentStatusRCN
                      );
                      setFieldValue(
                        "freightBackDetails",
                        initValues.freightBackDetails ?? ""
                      );
                    }}
                    onBlur={handleBlur}
                  />
                  {values?.doesShipperWantFreightBack === "Y" && (
                    <div className="tooltip-custom" data-testid="info-icon">
                      <TooltipWithClickAway
                        tooltipText="Obtain the LOA and cut a Return NOC suffix bill. In the body of the bill, enter the authorizing person’s first and last name, phone number, and email address. Be specific in the description of the freight being returned."
                        tooltipKey="tooltip4"
                      />
                    </div>
                  )}
                  <FormControlLabel
                    value="N"
                    control={<Radio disabled={formFieldsDisabled} />}
                    label="No"
                    data-testid="doesShipperWantFreightBackN"
                    checked={values?.doesShipperWantFreightBack === "N"}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("clearShipmentStatusRCN", undefined);
                      setFieldValue(
                        "valueRangeFollowUp",
                        initValues.valueRangeFollowUp
                      );
                      setFieldValue(
                        "freightBackDetails",
                        initValues.freightBackDetails ?? ""
                      );
                    }}
                    onBlur={handleBlur}
                    className={
                      values?.doesShipperWantFreightBack === "Y"
                        ? "margin-left-20"
                        : ""
                    }
                  />
                  {values?.doesShipperWantFreightBack === "N" && isRF && (
                    <div className="tooltip-custom">
                      <TooltipWithClickAway
                        tooltipText="Use the same verbiage in the on hand showing details about the undamaged freight."
                        tooltipKey="tooltip44"
                      />
                    </div>
                  )}
                </RadioGroup>
                {getErrorMessage("doesShipperWantFreightBack")}
                {(!isRF ||
                  (isRF && values?.doesShipperWantFreightBack === "Y")) && (
                  <Grid item md={12} xs={12}>
                    <FormControl fullWidth>
                      <TextField
                        autoComplete="off"
                        name="freightBackDetails"
                        data-testid="freightBackDetails"
                        className="mt-1"
                        value={values?.freightBackDetails}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Describe Steps Taken"
                        fullWidth
                        inputProps={{ maxLength: 150 }}
                        disabled={formFieldsDisabled}
                        variant="outlined"
                      />
                    </FormControl>
                    {getErrorMessage("freightBackDetails")}
                  </Grid>
                )}
              </FormControl>
            </Grid>
            {values?.doesShipperWantFreightBack === "N" && isRF && (
              <Grid item md={12} xs={12}>
                <NoteBox message="Issue the on-hand notice and follow On Hand procedures." />
              </Grid>
            )}

            {values?.doesShipperWantFreightBack === "Y" && !isPR && (
              <Grid item md={12} xs={12} className="pt-0">
                <FormControl className="radio-group question-group">
                  <FormLabel>
                    <FormControlLabel
                      className="checkbox-shoartage"
                      control={
                        <Checkbox
                          data-testid="clearShipmentStatusRCN"
                          disableRipple
                          checked={values?.clearShipmentStatusRCN === "Y"}
                          disabled={formFieldsDisabled}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setFieldValue(
                              "clearShipmentStatusRCN",
                              e.target.checked ? "Y" : "N"
                            );
                          }}
                          name="clearShipmentStatusRCN"
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                        />
                      }
                      label=""
                    />{" "}
                    Clear your delivery exception pro number on the 86 screen to
                    show status RCN <sup>*</sup> &nbsp;
                  </FormLabel>
                  {values?.doesShipperWantFreightBack === "Y" &&
                    getErrorMessage("clearShipmentStatusRCN")}
                </FormControl>
              </Grid>
            )}
            {values.doesShipperWantFreightBack === "N" && !isRF && (
              <>
                <Grid item md={12} xs={12} className="pt-0">
                  <FormControl className="radio-group question-group">
                    <FormLabel>
                      User must select a value range <sup>*</sup>
                    </FormLabel>
                    <RadioGroup
                      row
                      name="valueRangeFollowUp"
                      data-testid="valueRangeFollowUp"
                      value={values?.valueRangeFollowUp ?? ""}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio disabled={formFieldsDisabled} />}
                        label="Less than $250.00"
                        data-testid="valueRangeFollowUp1"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio disabled={formFieldsDisabled} />}
                        label="$250 to $1,000"
                        data-testid="valueRangeFollowUp2"
                      />
                      <FormControlLabel
                        value="3"
                        control={<Radio disabled={formFieldsDisabled} />}
                        label="More than $1,000"
                        data-testid="valueRangeFollowUp3"
                      />
                      <div className="tooltip-custom">
                        <TooltipWithClickAway
                          tooltipText="Use the same verbiage in the On Hand showing details about the undamaged freight."
                          tooltipKey="tooltip5"
                        />
                      </div>
                    </RadioGroup>
                    {getErrorMessage("valueRangeFollowUp")}
                  </FormControl>
                </Grid>

                {valueRangeFollowUpMessage && (
                  <Grid item md={12} xs={12}>
                    <NoteBox message={valueRangeFollowUpMessage} />
                  </Grid>
                )}

                {!isRF && (
                  <Grid item md={12} xs={12} className="pt-0">
                    <FormControl className="radio-group question-group">
                      <FormLabel>
                        Did the RCPM approve a dump bill? <sup>*</sup>
                      </FormLabel>
                      <RadioGroup
                        row
                        name="didRcpmApproveDumpBill"
                        data-testid="didRcpmApproveDumpBill"
                        value={values?.didRcpmApproveDumpBill ?? ""}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value="Y"
                          control={<Radio disabled={formFieldsDisabled} />}
                          label="Yes"
                          data-testid="didRcpmApproveDumpBillY"
                          checked={values?.didRcpmApproveDumpBill === "Y"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio disabled={formFieldsDisabled} />}
                          label="No"
                          data-testid="didRcpmApproveDumpBillN"
                          checked={values?.didRcpmApproveDumpBill === "N"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </RadioGroup>
                      {getErrorMessage("didRcpmApproveDumpBill")}
                    </FormControl>
                  </Grid>
                )}

                {values?.didRcpmApproveDumpBill === "Y" && (
                  <Grid container spacing={2} className="padding-15">
                    <Grid item md={12} xs={12}>
                      <NoteBox message="Note: Shipper and consignee code are the 777 OS&D codes" />
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <NoteBox message="Note: Your TM must sign the delivery receipt" />
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <NoteBox message="Note: Hazmat requires the direction of your regional saftey manager" />
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            <Grid item md={12} xs={12}>
              <NoteBox message="Note: Hazmat, drums, or totes must have approval from Carissa Atkinson (catkinson@saia.com)" />
            </Grid>
            {values.doesShipperWantFreightBack === "N" && (
              <>
                <Grid item md={12} xs={12} className="pt-0">
                  <FormControl className="radio-group question-group">
                    <FormLabel>
                      Was return authorization received? <sup>*</sup>
                    </FormLabel>
                    <RadioGroup
                      row
                      name="wasReturnAuthorizationReceived"
                      data-testid="wasReturnAuthorizationReceived"
                      value={values?.wasReturnAuthorizationReceived ?? ""}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        value="Y"
                        control={<Radio disabled={formFieldsDisabled} />}
                        label="Yes"
                        data-testid="wasReturnAuthorizationReceivedY"
                        checked={values?.wasReturnAuthorizationReceived === "Y"}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <FormControlLabel
                        value="N"
                        control={<Radio disabled={formFieldsDisabled} />}
                        label="No"
                        data-testid="wasReturnAuthorizationReceivedN"
                        checked={values?.wasReturnAuthorizationReceived === "N"}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </RadioGroup>
                    {getErrorMessage("wasReturnAuthorizationReceived")}
                  </FormControl>
                </Grid>

                {values?.wasReturnAuthorizationReceived === "Y" && (
                  <Grid container spacing={2} className="padding-15">
                    <Grid item md={12} xs={12}>
                      <NoteBox message="Note: Cut a suffix" />
                    </Grid>
                  </Grid>
                )}

                {values?.wasReturnAuthorizationReceived === "N" && (
                  <Grid container spacing={2} className="padding-15">
                    <Grid item md={12} xs={12}>
                      <NoteBox message="Note: Issue an On Hand notice then, Clear the exception in the 78/6" />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </>

          <Grid item md={12} xs={12} className="pt-0">
            <FormControl fullWidth size="small">
              <InputLabel style={FORM_STYLES.LABEL} shrink={true}>
                F18 notes <sup>*</sup>
              </InputLabel>
              <TextField
                autoComplete="off"
                multiline
                rows={2}
                name="f18Note2"
                data-testid="f18Note2"
                value={values?.f18Note2}
                onChange={handleChange}
                placeholder="Enter F18 notes "
                fullWidth
                inputProps={{ maxLength: 300 }}
                disabled={formFieldsDisabled}
                variant="outlined"
              />
            </FormControl>
            {getErrorMessage("f18Note2")}
            <br />
            <br />
          </Grid>

          {/* Action Buttons */}
          {status !== EPRStatus.needsCleared && (
            <Grid item md={12} xs={12} className="btn-group-submit">
              {formFieldsDisabled ? (
                <Button
                  variant="contained"
                  onClick={enableEditing}
                  className="assign ml mr-0"
                  data-testid="edit-button"
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => saveOrComplete(EFormAction.followupSave)}
                    className="entry ml"
                    type="submit"
                    data-testid="save-button"
                  >
                    Save
                  </Button>
                  <Button
                    variant="contained"
                    onClick={resetToLastSaved}
                    className="clear-btn mr-0 ml"
                    disabled={loading}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => saveOrComplete(EFormAction.complete)}
                    className="entry ml"
                    type="submit"
                    data-testid="complete-image-button"
                  >
                    Complete & Image
                  </Button>
                  { hasOnHandPermission && <Button
                    variant="contained"
                    onClick={() => saveOrComplete(EFormAction.complete, true)}
                    className="entry ml"
                    type="submit"
                    data-testid="complete-image-enter-oh-button"
                  >
                    Complete & Image & Enter OH
                  </Button> }
                </>
              )}
            </Grid>
          )}
        </Grid>
      </form>
      <div className="hide-me" id={`damage-followup-image-temp`}>
        <PRInvestigationTemplate
          osdType={osdType}
          values={values}
          osdNumber={osdNumber}
          isFollowUp={true}
        />
      </div>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(DamageFollowUp);
